/**
 * Exact position P&L (SPEC §8 "Average price", §10.1 `unrealized_tick`). Pure, integer-only.
 *
 * A position is kept as its signed net quantity and `openCostTicks` = Σ price_ticks × qty over the
 * open contracts (proto `PositionUpdate.open_cost_ticks`). The average price is the rational
 * `openCostTicks / |netQty|` — after scaling in (1 @ 100 + 2 @ 101) it is 302/3 ticks, which no
 * float or rounded tick represents exactly — so P&L never divides:
 *
 *     long:  (|q| × price − C) × tick_value
 *     short: (C − |q| × price) × tick_value
 *
 * Everything is `bigint`; convert to a JS number only at the display edge.
 */

export type ExactPosition = {
  /** Signed: positive long, negative short, 0 flat. */
  netQty: number;
  /** Σ price_ticks × qty over the open contracts; 0 when flat. */
  openCostTicks: bigint;
};

const big = (v: bigint | number): bigint => (typeof v === "bigint" ? v : BigInt(Math.trunc(v)));

/** Signed open P&L in tick-contracts if price were `priceTicks` (0 when flat). */
export function openTickContracts(p: ExactPosition, priceTicks: bigint | number): bigint {
  if (p.netQty === 0) return 0n;
  const q = BigInt(Math.abs(p.netQty));
  const longTicks = q * big(priceTicks) - p.openCostTicks;
  return p.netQty > 0 ? longTicks : -longTicks;
}

/** Signed open P&L in integer cents at `priceTicks` (0 when flat). */
export function openPnlCents(
  p: ExactPosition,
  priceTicks: bigint | number,
  tickValueCents: bigint | number,
): bigint {
  return openTickContracts(p, priceTicks) * big(tickValueCents);
}

/** `num / den` rounded to the nearest integer, halves away from zero (domain `AvgPrice::round_ticks`). */
export function roundHalfAway(num: bigint, den: bigint): bigint {
  if (den === 0n) throw new RangeError("division by zero");
  const neg = num < 0n !== den < 0n;
  const n = num < 0n ? -num : num;
  const d = den < 0n ? -den : den;
  let q = n / d;
  if ((n % d) * 2n >= d) q += 1n;
  return neg ? -q : q;
}

function gcd(a: bigint, b: bigint): bigint {
  let x = a < 0n ? -a : a;
  let y = b < 0n ? -b : b;
  while (y !== 0n) [x, y] = [y, x % y];
  return x;
}

/** The exact average price in ticks as a reduced fraction, or null when flat. */
export function avgPrice(p: ExactPosition): { num: bigint; den: bigint } | null {
  if (p.netQty === 0) return null;
  const den = BigInt(Math.abs(p.netQty));
  const g = gcd(p.openCostTicks, den) || 1n;
  return { num: p.openCostTicks / g, den: den / g };
}

/** The ladder row that holds the average: the nearest tick, halves away from zero. */
export function avgRowTicks(p: ExactPosition): bigint | null {
  const a = avgPrice(p);
  return a === null ? null : roundHalfAway(a.num, a.den);
}

/**
 * The price open P&L is marked at: the last trade (SPEC §10.1 `last_ticks`), else the side the
 * position would exit into (bid for a long, ask for a short). Null when nothing is known.
 */
export function markTicks(
  netQty: number,
  last: number | null,
  bid: number | null,
  ask: number | null,
): number | null {
  if (last !== null) return last;
  if (netQty > 0) return bid;
  if (netQty < 0) return ask;
  return null;
}

/** Exact open position from a `PositionUpdate`, falling back to the rounded average when an old
 * gateway sends no `open_cost_ticks` (then `exact` is false and P&L is approximate). */
export function exactPositionOf(u: {
  netQty: number;
  avgPriceTicks: bigint;
  openCostTicks: bigint;
}): ExactPosition & {
  exact: boolean;
} {
  if (u.netQty === 0) return { netQty: 0, openCostTicks: 0n, exact: true };
  if (u.openCostTicks !== 0n) return { netQty: u.netQty, openCostTicks: u.openCostTicks, exact: true };
  return {
    netQty: u.netQty,
    openCostTicks: u.avgPriceTicks * BigInt(Math.abs(u.netQty)),
    exact: false,
  };
}
